/**
 * Notification Types & Interfaces
 * Defines the structure for all notification-related data
 */

export type NotificationKey =
    | "offer_created"
    | "order_placed"
    | "order_cancelled"
    | "order_delivered"
    | "payment_received"
    | "store_disabled"
    | "store_enabled"
    | "product_approved"
    | "product_rejected"
    | "review_added"
    | "customer_feedback"
    | "system_alert"
    | "owner_registered"
    | "merchant_registered"
    | "store_suggested"
    | "admin_action"
    | "promotion_ended";

export interface AdminNotification {
    id: number;
    content: string;
    key: NotificationKey;
    isRead: boolean;
    createdAt: string;
    metadata?: Record<string, any>; // For future extensibility (links, action data, etc.)
}

export interface NotificationsResponse {
    data: AdminNotification[];
    totalItems: number;
    totalPages: number;
}

export interface UnreadCountResponse {
    count: number;
}

export interface StoreNotification {
    id: number;
    content: string;
    source: "system" | "admin";
    key: string | null;
    type: "store" | "financial" | "cart";
    isRead: boolean;
    createdAt: string;
}

export interface StoreNotificationsResponse {
    data: StoreNotification[];
    totalItems: number;
    totalPages: number;
}

export interface MarkReadResponse {
    message: string;
}

// Pagination params
export interface NotificationPaginationParams {
    page: number;
    limit: number;
}

// Notification type categories for icons/colors
export type NotificationType =
    | "info"
    | "success"
    | "warning"
    | "error"
    | "system";

// Notification config for UI rendering
export interface NotificationConfig {
    type: NotificationType;
    icon: string; // Component name or icon key
    color: string; // Tailwind color class
    label: string; // i18n key
}

// Notification type mapping
export const NOTIFICATION_TYPE_MAP: Record<NotificationKey, NotificationConfig> = {
    offer_created: {
        type: "success",
        icon: "tag",
        color: "bg-green-50",
        label: "notifications.types.offer_created",
    },
    order_placed: {
        type: "success",
        icon: "shopping-bag",
        color: "bg-blue-50",
        label: "notifications.types.order_placed",
    },
    order_cancelled: {
        type: "error",
        icon: "x-circle",
        color: "bg-red-50",
        label: "notifications.types.order_cancelled",
    },
    order_delivered: {
        type: "success",
        icon: "check-circle",
        color: "bg-green-50",
        label: "notifications.types.order_delivered",
    },
    payment_received: {
        type: "success",
        icon: "credit-card",
        color: "bg-green-50",
        label: "notifications.types.payment_received",
    },
    store_disabled: {
        type: "error",
        icon: "alert-circle",
        color: "bg-red-50",
        label: "notifications.types.store_disabled",
    },
    store_enabled: {
        type: "success",
        icon: "check-circle",
        color: "bg-green-50",
        label: "notifications.types.store_enabled",
    },
    product_approved: {
        type: "success",
        icon: "check-circle",
        color: "bg-green-50",
        label: "notifications.types.product_approved",
    },
    product_rejected: {
        type: "error",
        icon: "x-circle",
        color: "bg-red-50",
        label: "notifications.types.product_rejected",
    },
    review_added: {
        type: "info",
        icon: "message-circle",
        color: "bg-blue-50",
        label: "notifications.types.review_added",
    },
    customer_feedback: {
        type: "info",
        icon: "message-square",
        color: "bg-blue-50",
        label: "notifications.types.customer_feedback",
    },
    system_alert: {
        type: "warning",
        icon: "alert-triangle",
        color: "bg-yellow-50",
        label: "notifications.types.system_alert",
    },
    owner_registered: {
        type: "success",
        icon: "user-plus",
        color: "bg-green-50",
        label: "notifications.types.owner_registered",
    },
    merchant_registered: {
        type: "success",
        icon: "building-2",
        color: "bg-green-50",
        label: "notifications.types.merchant_registered",
    },
    store_suggested: {
        type: "info",
        icon: "lightbulb",
        color: "bg-blue-50",
        label: "notifications.types.store_suggested",
    },
    admin_action: {
        type: "system",
        icon: "shield",
        color: "bg-purple-50",
        label: "notifications.types.admin_action",
    },
    promotion_ended: {
        type: "warning",
        icon: "alert-circle",
        color: "bg-yellow-50",
        label: "notifications.types.promotion_ended",
    },
};
